/**
 * indicators.js — Shared intraday indicator math
 * Used by SafeDayTab and GameDayTab.
 */

export function aggregateBars(bars, n) {
  const out = [];
  for (let i = 0; i + n <= bars.length; i += n) {
    const chunk = bars.slice(i, i + n);
    out.push({ time:chunk[0].time, open:chunk[0].open,
      high:Math.max(...chunk.map(b=>b.high)), low:Math.min(...chunk.map(b=>b.low)),
      close:chunk[chunk.length-1].close });
  }
  return out;
}

export function calcEMA(closes, period) {
  if (closes.length < period) return null;
  const k = 2/(period+1);
  let ema = closes.slice(0,period).reduce((s,c)=>s+c,0)/period;
  for (let i=period; i<closes.length; i++) ema = closes[i]*k + ema*(1-k);
  return ema;
}

export function calcEMASeries(closes, period) {
  if (closes.length < period) return [];
  const k = 2/(period+1);
  let v = closes.slice(0,period).reduce((a,b)=>a+b,0)/period;
  const out = [v];
  for (let i=period; i<closes.length; i++) { v=closes[i]*k+v*(1-k); out.push(v); }
  return out;
}

export function calcMACD(closes, fast=12, slow=26, signal=9) {
  if (closes.length < slow+signal-1) return null;
  const fS=calcEMASeries(closes,fast), sS=calcEMASeries(closes,slow);
  if (!fS.length||!sS.length) return null;
  const off=fS.length-sS.length;
  const ml=sS.map((s,i)=>fS[off+i]-s);
  if (ml.length<signal) return null;
  const sig=calcEMASeries(ml,signal);
  const m=ml[ml.length-1], mP=ml[ml.length-2]??null;
  const s=sig[sig.length-1], sP=sig.length>=2?sig[sig.length-2]:null;
  const h=m-s, hP=(mP!=null&&sP!=null)?mP-sP:null;
  return { macd:m, signal:s, histogram:h, prevHistogram:hP,
    bearishCross:hP!=null&&hP>=0&&h<0, bullishCross:hP!=null&&hP<=0&&h>0 };
}

export function calcMACross(closes, fast=9, slow=20) {
  const e1=calcEMASeries(closes,fast), e2=calcEMASeries(closes,slow);
  if (e1.length<2||e2.length<2) return null;
  const c1=e1[e1.length-1],p1=e1[e1.length-2],c2=e2[e2.length-1],p2=e2[e2.length-2];
  return { ema_fast:c1, ema_slow:c2, bullish:c1>c2,
    bullishCross:p1<=p2&&c1>c2, bearishCross:p1>=p2&&c1<c2 };
}

export function calcStochastic(bars, kPeriod=14, dPeriod=3) {
  if (bars.length<kPeriod+dPeriod-1) return null;
  const kS=[];
  for (let i=kPeriod-1; i<bars.length; i++) {
    const w=bars.slice(i-kPeriod+1,i+1);
    const hi=Math.max(...w.map(b=>b.high)), lo=Math.min(...w.map(b=>b.low));
    kS.push(hi===lo?50:((bars[i].close-lo)/(hi-lo))*100);
  }
  if (kS.length<dPeriod) return null;
  const dS=[];
  for (let i=dPeriod-1; i<kS.length; i++) dS.push(kS.slice(i-dPeriod+1,i+1).reduce((a,b)=>a+b,0)/dPeriod);
  const k=kS[kS.length-1],kP=kS[kS.length-2]??null,d=dS[dS.length-1],dP=dS[dS.length-2]??null;
  return { k,d,prevK:kP,prevD:dP,
    bullishCross:kP!=null&&dP!=null&&kP<=dP&&k>d, bearishCross:kP!=null&&dP!=null&&kP>=dP&&k<d };
}

export function calcRSI(closes, period=14) {
  if (closes.length<=period) return null;
  let gSum=0,lSum=0;
  for (let i=1;i<=period;i++){const d=closes[i]-closes[i-1];if(d>0)gSum+=d;else lSum-=d;}
  let avgG=gSum/period,avgL=lSum/period;
  for (let i=period+1;i<closes.length;i++){
    const d=closes[i]-closes[i-1];
    avgG=(avgG*(period-1)+Math.max(0,d))/period;
    avgL=(avgL*(period-1)+Math.max(0,-d))/period;
  }
  return avgL===0?100:100-100/(1+avgG/avgL);
}

export function minsRequired(type, cfg) {
  const tf=cfg?.timeframeMins??1;
  switch(type){
    case 'macd':       return (cfg.slow+cfg.signal-1)*tf;
    case 'maCross':    return cfg.slowPeriod*tf;
    case 'stochastic': return (cfg.kPeriod+cfg.dPeriod-1)*tf;
    case 'rsiExit':    return (cfg.period+1)*tf;
    default:           return 0;
  }
}

/** Estimate debit spread value at current underlying price (linear intrinsic model). */
export function estimateDebitPnl(pos, currentPrice) {
  if (!pos?.active||!currentPrice) return null;
  const isBull    = pos.type==='call_debit';
  const longStr   = Number(pos.longStrike);
  const shortStr  = Number(pos.shortStrike);
  const premium   = Number(pos.premium);     // what you paid
  const contracts = Number(pos.contracts)||1;
  const width     = Math.abs(shortStr-longStr);
  const maxProfit = (width-premium)*100*contracts;
  const maxLoss   = premium*100*contracts;
  const breakeven = isBull ? longStr+premium : longStr-premium;

  // Intrinsic value of spread at current price
  let intrinsic;
  if (isBull) {
    intrinsic = Math.min(width, Math.max(0, currentPrice-longStr));
  } else {
    intrinsic = Math.min(width, Math.max(0, longStr-currentPrice));
  }
  const currentValue  = intrinsic*100*contracts;
  const pnl           = currentValue - maxLoss;
  const pctOfMax      = maxProfit>0 ? (pnl/maxProfit)*100 : 0;
  return { currentValue, pnl, maxProfit, maxLoss, breakeven, pctOfMax, width, premium, isBull };
}

// ── openDirectionScore — pre-existing function restored ──────────────────────
// Used by DirectionGauge to score ES futures + VIX + SPY pre-market direction.
export function openDirectionScore({ esPct, vixPct, spyPreMktPct }) {
  if (esPct == null && spyPreMktPct == null) return { score: null, label: 'No data', confidence: 0 };
  let score = 0;
  if (esPct        != null) score += Math.min(50,  Math.max(-50,  esPct * 20));
  if (spyPreMktPct != null) score += Math.min(30,  Math.max(-30,  spyPreMktPct * 15));
  if (vixPct       != null) score -= Math.min(20,  Math.max(-20,  vixPct * 5));  // rising VIX = bearish signal
  score = Math.round(Math.min(100, Math.max(-100, score)));
  const label = score > 40  ? 'Strong Bullish'
              : score > 15  ? 'Bullish'
              : score > -15 ? 'Neutral'
              : score > -40 ? 'Bearish'
              :                'Strong Bearish';
  const confidence = Math.round(Math.min(95, Math.abs(score)));
  return { score, label, confidence };
}

// ── classifyPreMarketVolume + spread — used by Watchlist ─────────────────────
export function classifyPreMarketVolume(preVol, avgVol) {
  if (!preVol || !avgVol) return { level: 'na', label: '—', color: 'var(--faint)' };
  const ratio = preVol / (avgVol / 390);
  if (ratio >= 5)  return { level: 'very-high', label: 'Very High', color: '#22c55e' };
  if (ratio >= 2)  return { level: 'high',      label: 'High',      color: '#86efac' };
  if (ratio >= 0.5)return { level: 'normal',    label: 'Normal',    color: 'var(--faint)' };
  return              { level: 'low',        label: 'Low',       color: '#fca5a5' };
}

/** Bid-ask spread % of mid price. */
export function spread(q) {
  if (!q?.ask || !q?.bid) return null;
  const mid = (q.ask + q.bid) / 2;
  return mid > 0 ? ((q.ask - q.bid) / mid) * 100 : null;
}

// ── sma — used by MAChart ─────────────────────────────────────────────────────
// Returns the simple moving average of the input array as a single number.
// MAChart pre-slices the window, so we just need the mean of what's given.
export function sma(data, period) {
  if (!data?.length) return null;
  const slice = period ? data.slice(-period) : data;
  if (!slice.length) return null;
  return slice.reduce((a, b) => a + b, 0) / slice.length;
}

// ── pivots — used by PivotPoints ─────────────────────────────────────────────
export function pivots(high, low, close) {
  const p = (high + low + close) / 3;
  return {
    pp: p,
    r1: 2*p - low,   s1: 2*p - high,
    r2: p + (high - low), s2: p - (high - low),
    r3: high + 2*(p - low), s3: low - 2*(high - p),
  };
}

// ── calcBBWidth — used by SafeDayTab ─────────────────────────────────────────
export function calcBBWidth(closes, period=20) {
  if (!closes || closes.length < period) return null;
  const sl   = closes.slice(-period);
  const mean = sl.reduce((a,b)=>a+b,0)/period;
  const std  = Math.sqrt(sl.reduce((s,c)=>s+(c-mean)**2,0)/period);
  return { width:(std*4)/mean*100, upper:mean+2*std, lower:mean-2*std, mean };
}

// ── Strike helpers — used by SafeDayTab / GameDayTab ─────────────────────────
export const floorStrike = v => Math.floor(v);
export const ceilStrike  = v => Math.ceil(v);

/** Spread width: 1.5× ATR rounded to nearest $1, clamped $2–$6. */
export function spreadWidth(atrPct, price) {
  if (!atrPct || !price) return 4;
  const atr = (atrPct / 100) * price;
  return Math.max(2, Math.min(6, Math.round(atr * 1.5)));
}
